/**
 * onboardingChecklist.ts — 신규 입사자 온보딩 체크리스트 API (MYB-275)
 *
 * 엔드포인트:
 *   GET    /api/v1/onboarding/templates
 *   POST   /api/v1/onboarding/templates
 *   PUT    /api/v1/onboarding/templates/:id
 *   DELETE /api/v1/onboarding/templates/:id
 *   GET    /api/v1/onboarding/employees/:id/tasks
 *   POST   /api/v1/onboarding/employees/:id/tasks
 *   PUT    /api/v1/onboarding/tasks/:id
 *   GET    /api/v1/onboarding/summary
 */
import { api } from './client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OnboardingTaskStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';

export interface OnboardingTemplate {
  id: number;
  title: string;
  category: string;
  sort_order: number;
  is_required: boolean;
  is_active: boolean;
  created_by?: string;
  created_at: number;
  updated_at: number;
}

export interface OnboardingTask {
  id: number;
  employee_id: string;
  template_id: number;
  status: OnboardingTaskStatus;
  assignee_id?: string;
  assignee_name?: string;
  notes?: string;
  completed_at?: number;
  created_at: number;
  updated_at: number;
  // Joined from template
  title: string;
  category: string;
  sort_order: number;
  is_required: boolean;
}

export interface EmployeeTasksResponse {
  employee_id: string;
  tasks: OnboardingTask[];
  grouped: Record<string, OnboardingTask[]>;
  total: number;
  completed: number;
  completion_rate: number;
}

export interface OnboardingEmployeeSummary {
  employee_id: string;
  total_tasks: number;
  completed_tasks: number;
  pending_required: number;
  completion_rate: number;
  status: 'completed' | 'in_progress' | 'not_started';
}

export interface OnboardingSummary {
  total_employees: number;
  fully_complete: number;
  in_progress: number;
  not_started: number;
  task_counts: {
    pending: number;
    in_progress: number;
    completed: number;
    skipped: number;
  };
  employees: OnboardingEmployeeSummary[];
}

export interface TemplateCreatePayload {
  title: string;
  category: string;
  sort_order?: number;
  is_required?: boolean;
}

export interface TemplateUpdatePayload {
  title?: string;
  category?: string;
  sort_order?: number;
  is_required?: boolean;
  is_active?: boolean;
}

export interface TaskUpdatePayload {
  status: OnboardingTaskStatus;
  assignee_id?: string;
  assignee_name?: string;
  notes?: string;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

function unwrap<T>(res: any): T {
  return res.data?.data ?? res.data;
}

export const onboardingChecklistApi = {
  // --- Templates ---

  getTemplates: async (includeInactive = false): Promise<{ templates: OnboardingTemplate[]; categories: string[]; total: number }> => {
    const res = await api.get('/api/v1/onboarding/templates', {
      params: { include_inactive: includeInactive },
    });
    return unwrap(res);
  },

  createTemplate: async (payload: TemplateCreatePayload): Promise<OnboardingTemplate> => {
    const res = await api.post('/api/v1/onboarding/templates', payload);
    return unwrap(res);
  },

  updateTemplate: async (id: number, payload: TemplateUpdatePayload): Promise<OnboardingTemplate> => {
    const res = await api.put(`/api/v1/onboarding/templates/${id}`, payload);
    return unwrap(res);
  },

  deleteTemplate: async (id: number): Promise<void> => {
    await api.delete(`/api/v1/onboarding/templates/${id}`);
  },

  // --- Employee tasks ---

  getEmployeeTasks: async (employeeId: string): Promise<EmployeeTasksResponse> => {
    const res = await api.get(`/api/v1/onboarding/employees/${employeeId}/tasks`);
    return unwrap(res);
  },

  initializeEmployeeTasks: async (employeeId: string): Promise<{ employee_id: string; tasks: OnboardingTask[]; total: number }> => {
    const res = await api.post(`/api/v1/onboarding/employees/${employeeId}/tasks`);
    return unwrap(res);
  },

  // --- Task update ---

  updateTask: async (taskId: number, payload: TaskUpdatePayload): Promise<OnboardingTask> => {
    const res = await api.put(`/api/v1/onboarding/tasks/${taskId}`, payload);
    return unwrap(res);
  },

  // --- Dashboard ---

  getSummary: async (): Promise<OnboardingSummary> => {
    const res = await api.get('/api/v1/onboarding/summary');
    return unwrap(res);
  },
};
